import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntakeWizard } from './pages/IntakeWizard';
import { StatusPage } from './pages/StatusPage';
import { PrivacidadePage } from './pages/PrivacidadePage';
import { TreinamentoPage } from './pages/TreinamentoPage';
import { Footer } from './components/layout/Footer';
import { Brand, ErrorBoundary } from '@aions/ui-primitives';
import './styles/global.css';

type Route = 'intake' | 'status' | 'privacidade' | 'treinamento';

export function App(): JSX.Element {
  const { t } = useTranslation();
  const [route, setRoute] = useState<Route>(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'status') return 'status';
    if (hash === 'privacidade') return 'privacidade';
    if (hash === 'treinamento') return 'treinamento';
    return 'intake';
  });
  // Demo design 2026-05-15 — quando route='intake', landing aparece antes do
  // wizard. Click no CTA principal entra no wizard. Não muda lógica do wizard.
  const [showWizard, setShowWizard] = useState(false);

  const navigate = (r: Route) => {
    window.location.hash = r === 'intake' ? '' : r;
    setRoute(r);
    if (r !== 'intake') setShowWizard(false);
  };

  return (
    <div className="app-container">
      {/* Skip-to-main link for keyboard users (WCAG 2.4.1). Visible on focus. */}
      <a className="skip-to-main" href="#main-content">
        {t('app.skip_to_main')}
      </a>
      <header className="app-header">
        <div className="header-content">
          <button
            className="logo-link"
            aria-label={t('app.title')}
            onClick={() => {
              navigate('intake');
              setShowWizard(false);
            }}
          >
            <Brand size="md" tagline={t('app.title')} />
          </button>
          <nav className="header-nav" aria-label="Navegação principal">
            <button
              className={`nav-btn ${route === 'status' ? 'active' : ''}`}
              onClick={() => navigate('status')}
            >
              Acompanhar denúncia
            </button>
          </nav>
        </div>
      </header>

      <main className="app-main" id="main-content" tabIndex={-1}>
        <ErrorBoundary
          title={t('error_boundary.title')}
          message={t('error_boundary.message')}
          reloadLabel={t('error_boundary.reload')}
        >
          {route === 'intake' && !showWizard && (
            <section className="landing-hero" aria-labelledby="landing-title">
              <span className="landing-eyebrow">VOCÊ ESTÁ EM UM ESPAÇO SEGURO</span>
              <h2 id="landing-title" className="landing-title">
                Conte o que aconteceu.
                <br />
                Sem dar seu nome. Sem deixar rastro.
              </h2>
              <p className="landing-lede">
                Este canal é anônimo e aberto a colaboradores, clientes, fornecedores e ao público
                em geral. Nenhum colega, gestor ou área de TI consegue identificar quem denuncia.
                Você fala. A gente escuta.
              </p>

              <p className="landing-lede">
                O canal existe para receber relatos de irregularidades e proteger quem relata de
                boa-fé. Ele integra o Sistema de Gestão de Compliance da empresa, conforme a{' '}
                <strong>ISO 37301</strong>, e opera em conformidade com a LGPD (Lei nº 13.709/2018),
                a Lei nº 14.457/2022 e a Lei Anticorrupção (Lei nº 12.846/2013).
              </p>

              <div className="landing-cta-row">
                <button
                  type="button"
                  className="landing-cta landing-cta-primary"
                  onClick={() => setShowWizard(true)}
                >
                  <span className="landing-cta-title">Fazer uma denúncia</span>
                  <span className="landing-cta-sub">
                    Leva cerca de 5 minutos. Você recebe um protocolo no final.
                  </span>
                </button>
                <button
                  type="button"
                  className="landing-cta landing-cta-secondary"
                  onClick={() => navigate('status')}
                >
                  <span className="landing-cta-title">Acompanhar denúncia</span>
                  <span className="landing-cta-sub">
                    Use seu protocolo + frase-passe para ver o andamento.
                  </span>
                </button>
              </div>

              <div className="landing-trust">
                <div className="landing-trust-item">
                  <span className="landing-trust-dot" aria-hidden="true" />
                  <div>
                    <strong>Sem coleta de IP</strong>
                    <span>Servidor não registra de onde você acessa.</span>
                  </div>
                </div>
                <div className="landing-trust-item">
                  <span className="landing-trust-dot" aria-hidden="true" />
                  <div>
                    <strong>Criptografia ponta-a-ponta</strong>
                    <span>Sua mensagem é cifrada antes de sair do navegador.</span>
                  </div>
                </div>
                <div className="landing-trust-item">
                  <span className="landing-trust-dot" aria-hidden="true" />
                  <div>
                    <strong>Sem pressa</strong>
                    <span>Comece, pause, volte depois. Nada é enviado até você confirmar.</span>
                  </div>
                </div>
              </div>
            </section>
          )}
          {route === 'intake' && showWizard && (
            <IntakeWizard onComplete={() => navigate('status')} />
          )}
          {route === 'status' && <StatusPage />}
          {route === 'privacidade' && <PrivacidadePage />}
          {route === 'treinamento' && <TreinamentoPage onBack={() => navigate('intake')} />}
        </ErrorBoundary>
      </main>

      <Footer onNavigate={navigate} />
    </div>
  );
}
